import { TradeDirection, TradeFlow } from "../types/enums";
import { UIView } from "../types/vizEnums";

export interface LocationChangeInput {
  /** Which role the user is changing. */
  changingRole: "importer" | "exporter";
  /** The newly selected location ID. */
  newLocationId: string;
  /** The current ID of the *other* location (the one not being changed). */
  otherLocationId: string;
  /** Current UI view. */
  view: UIView;
  /** Current trade direction. */
  currentTradeDirection: TradeDirection;
  /** The world group location ID (e.g. "group-1"). */
  worldGroupId: string;
}

export interface LocationChangeResult {
  tradeDirection: TradeDirection;
  /** When set, the *other* location must be forced to this value. */
  forceOtherLocationId?: string;
  /** When set, trade flow must be forced to this value. */
  forceTradeFlow?: TradeFlow;
}

/**
 * Pure function that computes the trade-direction side-effects of a
 * location change in the explore tool.
 *
 * Rules:
 * - In Markets view one location must always be World. If both would
 *   become non-World the *other* location is forced to World.
 * - When both locations are World, trade flow is forced to Gross.
 * - In Products view trade direction is derived from who is World.
 */
export function computeLocationChangeEffects(
  input: LocationChangeInput,
): LocationChangeResult {
  const {
    changingRole,
    newLocationId,
    otherLocationId,
    view,
    currentTradeDirection,
    worldGroupId,
  } = input;

  const isNewWorld = newLocationId === worldGroupId;
  const isOtherWorld = otherLocationId === worldGroupId;

  // Determine which is exporter / importer after the change
  const isWorldExporter =
    changingRole === "exporter" ? isNewWorld : isOtherWorld;
  const isWorldImporter =
    changingRole === "importer" ? isNewWorld : isOtherWorld;

  let tradeDirection = currentTradeDirection;
  let forceOtherLocationId: string | undefined;
  let forceTradeFlow: TradeFlow | undefined;

  if (view === UIView.Markets) {
    if (isWorldExporter && isWorldImporter) {
      // Both world — keep current or default, force Gross
      tradeDirection = currentTradeDirection || TradeDirection.Exports;
      forceTradeFlow = TradeFlow.Gross;
    } else if (!isWorldExporter && !isWorldImporter) {
      // Both non-world — force the *other* location to World
      forceOtherLocationId = worldGroupId;
      tradeDirection =
        changingRole === "importer"
          ? TradeDirection.Imports
          : TradeDirection.Exports;
    } else if (isWorldImporter && !isWorldExporter) {
      // Exporter is a country → viewing that country's exports
      tradeDirection = TradeDirection.Exports;
    } else {
      // Importer is a country → viewing that country's imports
      tradeDirection = TradeDirection.Imports;
    }
  } else if (view === UIView.Products) {
    if (isWorldExporter && isWorldImporter) {
      tradeDirection = currentTradeDirection || TradeDirection.Exports;
    } else if (isWorldExporter) {
      tradeDirection = TradeDirection.Imports;
    } else {
      tradeDirection = TradeDirection.Exports;
    }
  }

  return { tradeDirection, forceOtherLocationId, forceTradeFlow };
}
